import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import clsx from "clsx";

import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { View, Text, Button, Dialog, DropdownField, Tabs } from "../../components/base";
import type { Tab } from "../../components/base/Tabs";
import { Icon } from "../../components/base/Icon";
import { agentSessionActions } from "../../redux/reducers/agentSessionSlice";
import { AgentEnvironment } from "../../redux/reducers/agentEnvironmentSlice";
import { agentSessionEventActions } from "../../redux/reducers/agentSessionEventSlice";
import { AudioPlayer } from "../../components/AudioPlayer";
import { API_URL } from "../../config";
import { useSearchParam } from "../../hooks/useSearchParam";
import { useSearchParams } from "../../hooks/useSearchParams";
import { SessionFilter } from "../../components/SessionFilter";
import { AgentSessionStatusPill } from "../../components/AgentSessionStatusPill";
import { AgentSessionDirectionPill } from "../../components/AgentSessionDirectionPill";
import { AgentSessionOriginPill } from "../../components/AgentSessionOriginPill";
import { AgentEnvironmentPill } from "../../components/AgentEnvironmentPill";
import { AgentSessionTranscript } from "../../components/AgentSessionTranscript";
import { organizationTelephonyActions } from "../../redux/reducers/organizationTelephonySlice";
import { useApiRequest } from "../../hooks/useApiRequest";
import { evaluatorActions, Evaluator } from "../../redux/reducers/evaluatorSlice";
import { Evaluation, sessionEvaluationActions } from "../../redux/reducers/evaluationSlices";
import { EvaluationListItem } from "../../components/evaluations/EvaluationListItem";
import { EvaluationDetailView } from "../../components/evaluations/EvaluationDetailView";
import { ExportTranscriptsDialog } from "../../components/ExportTranscriptsDialog";
import { AgentDeployment, agentDeploymentActions } from "../../redux/reducers/agentDeploymentSlice";
import { SessionTagList } from "../../components/SessionTagList";
import { SessionTraceViewer } from "../../components/SessionTraceViewer";
import { getTelephonyProviderInfo } from "../../utils/telephony";

const detailPanelVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "tween",
      duration: 0.3,
      ease: "easeInOut",
    },
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: {
      type: "tween",
      duration: 0.3,
      ease: "easeInOut",
    },
  },
};

export const AgentSessionsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const sessionId = useSearchParam("s");
  const tabParam = useSearchParam("tab");
  const { addSearchParam, searchParams } = useSearchParams();
  const [sessionFilters, setSessionFilters] = useState<Record<string, Record<string, unknown>>>({});
  const [isEvaluateDialogOpen, setIsEvaluateDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [selectedEvaluatorId, setSelectedEvaluatorId] = useState<string>("");
  const [activeDetailTab, setActiveDetailTab] = useState<string>("transcript");
  const [recordingUrl, setRecordingUrl] = useState("");
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null);
  const agentEnvironmentEntities = useAppSelector((state) => state.agentEnvironment.entities);
  const agentSessionEntities = useAppSelector((state) => state.agentSession.entities);
  const isLoading = useAppSelector((state) => state.agentSession.isLoading);
  const metadata = useAppSelector((state) => state.agentSession.metadata);
  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);
  const evaluatorsIsLoading = useAppSelector((state) => state.evaluator.isLoading);
  const sessionEvaluationEntities = useAppSelector((state) => state.sessionEvaluation.entities);
  const agentSessionEventEntities = useAppSelector((state) => state.agentSessionEvent.entities);
  const sessionEvaluationsIsLoading = useAppSelector((state) => state.sessionEvaluation.isLoading);
  const agentDeploymentEntities = useAppSelector((state) => state.agentDeployment.entities);
  const runEvaluatorRequest = useApiRequest<{ data: Evaluation[] }>();

  const agentSessions = useMemo(() => Object.values(agentSessionEntities).filter((session) => session.agentId === agentId), [agentSessionEntities, agentId]);
  const selectedSession = useMemo(() => agentSessions.find((session) => session.id === sessionId), [agentSessions, sessionId]);
  const agentEnvironments = useMemo(
    () => Object.values(agentEnvironmentEntities).filter((environment) => environment.agentId === agentId),
    [agentEnvironmentEntities, agentId],
  );
  const evaluators = useMemo(() => Object.values(evaluatorEntities), [evaluatorEntities]);
  const sessionEvents = useMemo(
    () => Object.values(agentSessionEventEntities).filter((event) => event.agentSessionId === selectedSession?.id),
    [agentSessionEventEntities, selectedSession?.id],
  );
  const sessionTranscript = useMemo(
    () => Object.values(agentSessionEventEntities).filter((event) => event.agentSessionId === selectedSession?.id && event.name === "transcript_part"),
    [agentSessionEventEntities, selectedSession?.id],
  );
  const sessionEvaluations = useMemo(
    () => Object.values(sessionEvaluationEntities).filter((evaluation) => evaluation.sessionId === selectedSession?.id),
    [sessionEvaluationEntities, selectedSession?.id],
  );
  const selectedEvaluation = useMemo(
    () => sessionEvaluations.find((evaluation) => evaluation.id === selectedEvaluationId) || null,
    [sessionEvaluations, selectedEvaluationId],
  );

  const getRecordingUrl = async () => {
    const response = await fetch(`${API_URL}/v1/agents/${agentId}/sessions/${sessionId}/recording`, {
      credentials: "include",
    });

    // For web recordings, we respond with a JSON object containing the url
    // For telephony recordings, we respond with the content directly.
    if (response.ok) {
      try {
        const responseJson = await response.json();
        setRecordingUrl(responseJson.url);
      } catch (error) {
        setRecordingUrl(`${API_URL}/v1/agents/${agentId}/sessions/${sessionId}/recording`);
      }
    }
  };

  // Check if recording is available
  useEffect(() => {
    setRecordingUrl("");

    if (sessionId && agentId) {
      getRecordingUrl();
    }
  }, [sessionId, agentId]);

  useEffect(() => {
    if (isEvaluateDialogOpen && evaluators.length > 0 && !selectedEvaluatorId) {
      setSelectedEvaluatorId(evaluators[0].id);
    }
  }, [isEvaluateDialogOpen, evaluators, selectedEvaluatorId]);

  useEffect(() => {
    if (agentId && selectedSession?.id) {
      dispatch(
        sessionEvaluationActions.get({
          agentId,
          sessionId: selectedSession.id,
        }),
      );
    }
  }, [dispatch, agentId, selectedSession?.id]);

  useEffect(() => {
    if (runEvaluatorRequest.data && !runEvaluatorRequest.error && agentId && selectedSession?.id) {
      dispatch(
        sessionEvaluationActions.get({
          agentId,
          sessionId: selectedSession.id,
        }),
      );
    }
  }, [runEvaluatorRequest.data, runEvaluatorRequest.error, dispatch, agentId, selectedSession?.id]);

  useEffect(() => {
    dispatch(evaluatorActions.get({}));
    if (selectedSession?.id) {
      dispatch(agentSessionEventActions.get({ agentId, agentSessionId: selectedSession.id, limit: "1000" }));
    }
  }, [selectedSession?.id]);

  const detailTabs: Tab[] = [
    { id: "events", label: "Events", icon: "event" },
    { id: "transcript", label: "Transcript", icon: "file" },
    { id: "trace", label: "Trace", icon: "activity" },
    { id: "evaluations", label: "Evaluations", icon: "checklist" },
  ];

  const normalizedTabParam = useMemo(() => {
    if (tabParam === "session") {
      return "events";
    }
    if (tabParam === "evals") {
      return "evaluations";
    }
    return tabParam;
  }, [tabParam]);

  useEffect(() => {
    const availableTabIds = detailTabs.map((tab) => tab.id);
    const nextTabFromUrl = normalizedTabParam && availableTabIds.includes(normalizedTabParam) ? normalizedTabParam : "";

    if (nextTabFromUrl && nextTabFromUrl !== activeDetailTab) {
      setActiveDetailTab(nextTabFromUrl);
      return;
    }

    if (!nextTabFromUrl && !availableTabIds.includes(activeDetailTab) && availableTabIds[0]) {
      setActiveDetailTab(availableTabIds[0]);
    }
  }, [detailTabs, normalizedTabParam, activeDetailTab]);

  const handleDetailTabChange = (tabId: string) => {
    setActiveDetailTab(tabId);
    addSearchParam("tab", tabId);
  };

  // Get telephony configurations for the organization
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();
  const organizationTelephonyEntities = useAppSelector((state) => state.organizationTelephony.entities);
  const organizationTelephony = useMemo(
    () => (effectiveOrganizationId ? Object.values(organizationTelephonyEntities).filter((config) => config.organizationId === effectiveOrganizationId) : []),
    [organizationTelephonyEntities, effectiveOrganizationId],
  );

  const telephonyConfig = organizationTelephony.find((config) => config.id === selectedSession?.telephonyId);
  const telephonyProviderInfo = getTelephonyProviderInfo(telephonyConfig?.providerType);

  const onChangeSessionFilter = (newFilters: Record<string, Record<string, unknown>>) => {
    // Attempt to convert environment name to ID, otherwise use the value as is
    if (newFilters?.agentEnvironmentId) {
      Object.entries(newFilters.agentEnvironmentId).forEach(([key, value]) => {
        const agentEnvironment = agentEnvironments.find((env) => env.name === value);
        if (agentEnvironment) {
          newFilters.agentEnvironmentId[key] = agentEnvironment.id;
        } else {
          newFilters.agentEnvironmentId[key] = value;
        }
      });
    }

    // Attempt to convert createdAt to ISO string based on browser timezone.
    if (newFilters?.createdAt) {
      Object.entries(newFilters.createdAt).forEach(([key, value]) => {
        try {
          newFilters.createdAt[key] = new Date(value as string).toISOString();
        } catch {
          newFilters.createdAt[key] = value;
        }
      });
    }

    setSessionFilters(newFilters);
  };

  const filteredSessions = agentSessions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Calculate pagination using metadata
  const totalPages = Math.ceil(metadata.total / metadata.limit);
  const currentPage = metadata.page;

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getEnvironmentById = (environmentId: string): AgentEnvironment | undefined => {
    return agentEnvironments.find((env) => env.id === environmentId);
  };

  const getEnvironmentName = (environmentId: string): string => {
    const environment = getEnvironmentById(environmentId);
    return environment ? environment.name : "Unknown Environment";
  };

  const getDeploymentById = (deploymentId: string | undefined): AgentDeployment | undefined => {
    return agentDeploymentEntities[deploymentId || ""];
  };

  const getDeploymentInfo = (deploymentId: string | undefined): { name: string; status: string; deployedAt?: string } | null => {
    const deployment = getDeploymentById(deploymentId);
    if (!deployment) return null;
    return {
      name: `deployment-${deployment.id.split("-")[0]}`,
      status: deployment.status,
      deployedAt: deployment.deployedAt,
    };
  };

  const downloadEventsCsv = () => {
    if (!selectedSession) return;

    // Create a download link for the transcript
    const link = document.createElement("a");
    link.href = `${API_URL}/v1/agents/${agentId}/sessions/${selectedSession.id}/formattedEvents?format=csv`;
    link.download = `events_${selectedSession.id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadEventsJson = () => {
    if (!selectedSession) return;

    // Open the transcript in a new tab instead of downloading
    window.open(`${API_URL}/v1/agents/${agentId}/sessions/${selectedSession.id}/formattedEvents?format=json`, "_blank");
  };

  const downloadTranscriptCsv = () => {
    if (!selectedSession) return;

    // Create a download link for the transcript
    const link = document.createElement("a");
    link.href = `${API_URL}/v1/agents/${agentId}/sessions/${selectedSession.id}/formattedEvents?filter=transcript&format=csv`;
    link.download = `transcript_${selectedSession.id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTranscriptJson = () => {
    if (!selectedSession) return;

    // Open the transcript in a new tab instead of downloading
    window.open(`${API_URL}/v1/agents/${agentId}/sessions/${selectedSession.id}/formattedEvents?filter=transcript&format=json`, "_blank");
  };

  const downloadTraceJson = () => {
    if (!selectedSession) return;

    // Open the trace in OTLP format in a new tab
    window.open(`${API_URL}/v1/agents/${agentId}/sessions/${selectedSession.id}/trace-full?format=otlp`, "_blank");
  };

  const handlePageChange = (page: number) => {
    navigate(`/agents/${agentId}/sessions`);

    dispatch(
      agentSessionActions.get({
        agentId,
        page: page.toString(),
        limit: "100",
        ...sessionFilters,
      }),
    );
  };

  // Load telephony configurations and deployments
  useEffect(() => {
    if (effectiveOrganizationId) {
      dispatch(organizationTelephonyActions.get({ organizationId: effectiveOrganizationId }));
    }

    if (agentId) {
      dispatch(agentDeploymentActions.get({ agentId, page: "1", limit: "100" }));
    }
  }, [effectiveOrganizationId, agentId]);

  // Reset to first page when filters change
  useEffect(() => {
    if (agentId) {
      dispatch(
        agentSessionActions.get({
          agentId,
          page: "1",
          limit: "100",
          ...sessionFilters,
        }),
      );
    }
  }, [agentId, sessionFilters]);

  // Determine if metrics summary exists
  const metrics = (selectedSession as any)?.metrics as Record<string, unknown> | undefined;
  const hasLatencySummary = Boolean(
    metrics &&
      ((metrics as any)?.latency?.turn_taking_summary?.p50 != null ||
        (metrics as any)?.latency?.turn_taking_summary?.p95 != null ||
        (metrics as any)?.latency?.turn_taking_summary?.p99 != null ||
        ((metrics as any)?.latency?.turn_taking_summary?.count ?? 0) > 0),
  );

  // Extract latency metrics for tooltip
  const getLatencyMetric = (path: string) => {
    try {
      const parts = path.split(".");
      let cur: any = metrics || {};
      for (const p of parts) cur = cur?.[p];
      const num = typeof cur === "number" ? cur : Number(cur);
      return Number.isFinite(num) ? num : null;
    } catch {
      return null;
    }
  };

  const p50 = getLatencyMetric("latency.turn_taking_summary.p50");
  const p95 = getLatencyMetric("latency.turn_taking_summary.p95");
  const p99 = getLatencyMetric("latency.turn_taking_summary.p99");
  const turnCount = getLatencyMetric("latency.turn_taking_summary.count");
  const formatMs = (s: number | null) => (s == null ? "—" : `${Math.round(s * 1000)} ms`);

  // Poll for session details until latency summary is available (or timeout)
  useEffect(() => {
    if (!selectedSession) return;
    if (hasLatencySummary) return; // Already present; no polling needed

    let attempts = 0;
    const maxAttempts = 40; // ~100 seconds at 2.5s interval
    const intervalId = window.setInterval(() => {
      attempts += 1;
      dispatch(
        agentSessionActions.getById({
          agentId: selectedSession.agentId,
          id: selectedSession.id,
        }) as any,
      );
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [selectedSession?.id, hasLatencySummary]);

  return (
    <View className="relative flex flex-col h-full overflow-hidden">
      <View className="flex flex-col gap-1 p-4 border-b border-gray-200 dark:border-gray-700">
        <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter Sessions</Text>
        <View className="flex flex-row items-center gap-3">
          <SessionFilter
            onChange={onChangeSessionFilter}
            label=""
            placeholder="Try: status IN ('completed', 'in_progress') AND environment = 'prod'"
            className="flex-1"
          />
          <Button
            onClick={() => setIsExportDialogOpen(true)}
            variant="ghost"
            className="hidden sm:flex items-center gap-2 h-10 !py-2 !px-3 mt-1"
          >
            <Icon
              name="download"
              size={16}
              className="text-gray-500"
            />
            <Text className="text-sm">Download Transcripts</Text>
          </Button>
        </View>
      </View>

      <View className="flex flex-row flex-1 overflow-hidden">
        <View className={clsx("flex flex-col flex-1 p-4 transition-all duration-300 overflow-hidden", selectedSession ? "w-1/3" : "w-full")}>
          {isLoading && !filteredSessions.length ? (
            <View className="flex justify-center items-center h-32">
              <Text>Loading sessions...</Text>
            </View>
          ) : filteredSessions.length === 0 ? (
            <View className="flex justify-center items-center h-32 bg-gray-100 dark:bg-gray-800 rounded-md">
              <Text className="text-gray-500 dark:text-gray-400">No sessions found</Text>
            </View>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
              className="flex-1 border border-gray-200 dark:border-gray-700 rounded-md overflow-y-auto"
            >
              {filteredSessions.map((session, index) => (
                <View
                  key={session.id}
                  onClick={() => {
                    const nextSearchParams = new URLSearchParams(searchParams);
                    nextSearchParams.set("s", session.id);
                    navigate(`/agents/${agentId}/sessions?${nextSearchParams.toString()}`);
                  }}
                  className={`py-2 px-3 cursor-pointer transition-colors ${
                    selectedSession?.id === session.id
                      ? "bg-indigo-100 dark:bg-indigo-900"
                      : "bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  } ${index !== 0 ? "border-t border-gray-200 dark:border-gray-700" : ""}`}
                >
                  <View className="flex flex-row justify-between items-center">
                    <View className="flex flex-col">
                      <View className="flex flex-row items-center gap-2">
                        <Text className="font-medium text-sm">{formatDate(session.createdAt)}</Text>
                        {session.status && <AgentSessionStatusPill status={session.status} />}
                        {session.direction && <AgentSessionDirectionPill direction={session.direction} />}
                        {session.origin && session.origin.toLowerCase() !== "unknown" && <AgentSessionOriginPill origin={session.origin} />}
                        {!selectedSession && session.tags && session.tags.length > 0 && (
                          <SessionTagList
                            tags={session.tags}
                            maxVisible={2}
                          />
                        )}
                      </View>
                      <View className="flex flex-row items-center gap-2 mt-1">
                        <AgentEnvironmentPill
                          environment={getEnvironmentName(session.agentEnvironmentId)}
                          size="small"
                          variant="muted"
                        />
                        {session.deploymentId && getDeploymentInfo(session.deploymentId) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/agents/${agentId}/deployments?deployment=${session.deploymentId}`);
                            }}
                            variant="ghost"
                            className="!p-1 !h-auto hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors rounded"
                          >
                            <View className="flex items-center gap-1">
                              <Icon
                                name="arrow-up"
                                size={12}
                                className="text-blue-500"
                              />
                              <Text className="text-xs text-blue-600 dark:text-blue-400 hover:underline">{getDeploymentInfo(session.deploymentId)?.name}</Text>
                            </View>
                          </Button>
                        )}
                      </View>
                    </View>
                    <Icon
                      name="chevron-right"
                      size={16}
                      className="text-gray-400"
                    />
                  </View>
                </View>
              ))}
            </motion.div>
          )}

          {totalPages > 1 && (
            <View className="flex flex-row justify-center items-center mt-4 px-2">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
                variant="ghost"
                className="flex items-center !p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Icon
                  name="chevron-left"
                  size={16}
                  className={currentPage === 1 ? "text-gray-300 dark:text-gray-600" : "text-gray-500 dark:text-gray-400"}
                />
              </Button>

              <View className="flex flex-row items-center gap-1">
                {(() => {
                  // Logic to show 5 pages max at a time
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);

                  // Adjust if we're near the end
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                    <Button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      variant={currentPage === page ? "brand" : "ghost"}
                      className={`!min-w-[32px] !h-8 !p-0 ${
                        currentPage === page ? "!text-white" : "!text-gray-500 dark:!text-gray-400 hover:!bg-gray-100 dark:hover:!bg-gray-800"
                      }`}
                    >
                      <Text className={currentPage === page ? "text-white" : ""}>{page}</Text>
                    </Button>
                  ));
                })()}
              </View>

              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
                variant="ghost"
                className="flex items-center !p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Icon
                  name="chevron-right"
                  size={16}
                  className={currentPage === totalPages ? "text-gray-300 dark:text-gray-600" : "text-gray-500 dark:text-gray-400"}
                />
              </Button>
            </View>
          )}
        </View>

        {selectedSession && (
          <motion.div
            className="w-2/3 flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 z-10 overflow-hidden"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={detailPanelVariants}
          >
            <View className="flex flex-row justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <View className="flex flex-row items-center gap-3">
                {selectedSession.status && <AgentSessionStatusPill status={selectedSession.status} />}
                {selectedSession.direction && <AgentSessionDirectionPill direction={selectedSession.direction} />}
                {selectedSession.origin && selectedSession.origin.toLowerCase() !== "unknown" && <AgentSessionOriginPill origin={selectedSession.origin} />}
                <Text className="text-sm text-gray-500 dark:text-gray-400">{formatDate(selectedSession.createdAt)}</Text>
                <AgentEnvironmentPill environment={getEnvironmentName(selectedSession.agentEnvironmentId)} />
              </View>
              <View className="flex flex-row items-center gap-2">
                {selectedSession.telephonyCallId && (
                  <View className="group relative">
                    <Button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        // Add your usage statistics action here
                      }}
                      variant="ghost"
                      className="!p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Icon
                        name="phone"
                        size={16}
                        className="text-gray-500 dark:text-gray-400"
                      />
                    </Button>
                    <View className="absolute z-50 top-full right-0 min-w-56 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-left opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity">
                      <Text className="text-xs font-medium mb-2">Telephony Information</Text>
                      <View className="flex flex-col gap-2">
                        <View className="flex flex-row items-center gap-2">
                          <View className="flex items-center gap-2">
                            <Icon
                              name={telephonyProviderInfo.icon}
                              size={14}
                              className="text-gray-700 dark:text-gray-300"
                            />
                            <Text className="text-xs text-gray-700 dark:text-gray-300">{telephonyProviderInfo.name}</Text>
                          </View>
                        </View>
                        <View className="flex flex-row items-center gap-2">
                          <Icon
                            name="phone"
                            size={14}
                            className="text-gray-500 dark:text-gray-400"
                          />
                          <Text className="text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{selectedSession.telephonyCallId}</Text>
                        </View>
                      </View>
                    </View>
                  </View>
                )}
                <View className="group relative">
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      // Add your usage statistics action here
                    }}
                    variant="ghost"
                    className="!p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Icon
                      name="bar-chart-2"
                      size={16}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  </Button>
                  <View className="absolute z-50 top-full right-0 w-64 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-left opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity">
                    <Text className="text-xs font-medium mb-2">Usage Statistics</Text>
                    <View className="flex flex-col gap-1 mb-3">
                      <View className="flex flex-row justify-between">
                        <Text className="text-xs text-gray-500 dark:text-gray-400">Session Duration:</Text>
                        <Text className="text-xs">{((selectedSession.usage?.sessionDuration || 0) / 1000).toFixed(3)} s</Text>
                      </View>
                      <View className="flex flex-row justify-between">
                        <Text className="text-xs text-gray-500 dark:text-gray-400">Total Agent Speech Duration:</Text>
                        <Text className="text-xs">{((selectedSession.usage?.ttsDuration || 0) / 1000).toFixed(3)} s</Text>
                      </View>
                      <View className="flex flex-row justify-between">
                        <Text className="text-xs text-gray-500 dark:text-gray-400">Total Handler Processing Time:</Text>
                        <Text className="text-xs">{((selectedSession.usage?.moduleDuration || 0) / 1000).toFixed(3)} s</Text>
                      </View>
                    </View>
                    {hasLatencySummary && (
                      <>
                        <View className="border-t border-gray-200 dark:border-gray-600 pt-2 mb-2">
                          <Text className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Turn Taking Latency</Text>
                        </View>
                        <View className="flex flex-col gap-1">
                          <View className="flex flex-row justify-between">
                            <Text className="text-xs text-gray-500 dark:text-gray-400">P50:</Text>
                            <Text className="text-xs">{formatMs(p50)}</Text>
                          </View>
                          <View className="flex flex-row justify-between">
                            <Text className="text-xs text-gray-500 dark:text-gray-400">P95:</Text>
                            <Text className="text-xs">{formatMs(p95)}</Text>
                          </View>
                          <View className="flex flex-row justify-between">
                            <Text className="text-xs text-gray-500 dark:text-gray-400">P99:</Text>
                            <Text className="text-xs">{formatMs(p99)}</Text>
                          </View>
                          <View className="flex flex-row justify-between">
                            <Text className="text-xs text-gray-500 dark:text-gray-400">Total Turns:</Text>
                            <Text className="text-xs">{turnCount ?? "—"}</Text>
                          </View>
                        </View>
                      </>
                    )}
                  </View>
                </View>
                <Button
                  onClick={() => navigate(`/agents/${agentId}/sessions`)}
                  variant="ghost"
                  className="!p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <Icon
                    name="close"
                    size={16}
                  />
                </Button>
              </View>
            </View>
            <View className="flex flex-col flex-1 p-4 space-y-4 overflow-y-auto pb-4 max-h-full mb-4">
              {recordingUrl && (
                <View className="flex flex-col">
                  <View className="flex flex-row items-center gap-2 mb-2">
                    <Icon
                      name="headphones"
                      size={16}
                      className="text-gray-400"
                    />
                    <Text className="text-sm font-medium">Recording</Text>
                  </View>
                  <View className="flex flex-col bg-gray-50 dark:bg-gray-800 rounded-xl p-2 border border-gray-200 dark:border-gray-700">
                    <AudioPlayer
                      audioUrl={recordingUrl}
                      className="!w-full translate-x-0 opacity-100 !border-none !bg-transparent !shadow-none !p-0 !py-1"
                      errorMessage="No recording available"
                      filename={`${selectedSession?.id}.mp3`}
                    />
                  </View>
                </View>
              )}
              <View className="flex flex-col flex-1 min-h-[400px]">
                <View className="flex flex-col flex-1 min-h-[500px]">
                  <View className="flex flex-row items-center justify-between mb-2">
                    <Tabs
                      tabs={detailTabs}
                      activeTab={activeDetailTab}
                      onChange={handleDetailTabChange}
                    />
                    {["events", "transcript", "trace"].includes(activeDetailTab) ? (
                      <View className="flex flex-row items-center gap-3">
                        {["events", "transcript"].includes(activeDetailTab) && (
                          <Button
                            onClick={activeDetailTab === "events" ? downloadEventsCsv : downloadTranscriptCsv}
                            variant="ghost"
                            className="flex items-center !py-1 !px-2 text-xs mt-1 mr-1"
                          >
                            <Icon
                              name="download"
                              size={14}
                              className="text-gray-400 mt-[2px] mr-1"
                            />
                            <Text className="text-xs">Download CSV</Text>
                          </Button>
                        )}
                        <Button
                          onClick={
                            activeDetailTab === "events" ? downloadEventsJson : activeDetailTab === "transcript" ? downloadTranscriptJson : downloadTraceJson
                          }
                          variant="ghost"
                          className="flex items-center !py-1 !px-2 text-xs mt-1 mr-1"
                        >
                          <Icon
                            name="open"
                            size={14}
                            className="text-gray-400 mt-[2px] mr-1"
                          />
                          <Text className="text-xs">View JSON</Text>
                        </Button>
                      </View>
                    ) : null}

                    {activeDetailTab === "evaluations" ? (
                      <View className="flex flex-row items-center gap-2">
                        <Button
                          onClick={() => {
                            if (!isEvaluateDialogOpen) {
                              setSelectedEvaluatorId((prev) => prev || evaluators[0]?.id || "");
                              runEvaluatorRequest.clear();
                            }
                            setIsEvaluateDialogOpen(true);
                          }}
                          variant="ghost"
                          className="flex items-center !py-1 !px-2 text-xs"
                        >
                          <Icon
                            name="sparkles"
                            size={14}
                            className="text-indigo-500 mt-[2px] mr-1"
                          />
                          <Text className="text-xs">Evaluate</Text>
                        </Button>
                      </View>
                    ) : null}
                  </View>

                  {activeDetailTab === "events" ? (
                    <View className="flex flex-col flex-1 bg-gray-50 dark:bg-gray-800 rounded-xl p-3 overflow-y-auto border border-gray-200 dark:border-gray-700">
                      <AgentSessionTranscript events={selectedSession?.events && selectedSession.events.length > 0 ? selectedSession.events : sessionEvents} />
                    </View>
                  ) : null}

                  {activeDetailTab === "transcript" ? (
                    <View className="flex flex-col flex-1 bg-gray-50 dark:bg-gray-800 rounded-xl p-3 overflow-y-auto border border-gray-200 dark:border-gray-700">
                      <AgentSessionTranscript events={sessionTranscript || []} />
                    </View>
                  ) : null}

                  {activeDetailTab === "evaluations" ? (
                    <View className="flex flex-col flex-1 overflow-y-auto">
                      {sessionEvaluationsIsLoading ? (
                        <Text className="text-sm text-gray-500 dark:text-gray-400 p-3">Loading evaluations...</Text>
                      ) : sessionEvaluations.length === 0 ? (
                        <Text className="text-sm text-gray-500 dark:text-gray-400 p-3">No evaluations have been recorded for this session yet.</Text>
                      ) : (
                        <View className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
                          {sessionEvaluations.map((evaluation) => (
                            <EvaluationListItem
                              key={evaluation.id}
                              evaluation={evaluation}
                              evaluator={evaluators.find((item) => item.id === evaluation.evaluatorId) || null}
                              agentEnvironmentEntities={agentEnvironmentEntities}
                              isActive={selectedEvaluationId === evaluation.id}
                              onClick={(id) => setSelectedEvaluationId(id)}
                              sessionEnvironmentId={selectedSession?.agentEnvironmentId}
                              sessionOrigin={selectedSession?.origin}
                            />
                          ))}
                        </View>
                      )}
                    </View>
                  ) : null}

                  {activeDetailTab === "trace" ? (
                    <View className="flex flex-col flex-1 bg-gray-50 dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                      <SessionTraceViewer
                        agentId={agentId}
                        sessionId={selectedSession.id}
                      />
                    </View>
                  ) : null}
                </View>
              </View>
            </View>
          </motion.div>
        )}
      </View>
      <Dialog
        isOpen={isEvaluateDialogOpen}
        onClose={() => {
          setIsEvaluateDialogOpen(false);
          runEvaluatorRequest.clear();
        }}
        title="Evaluate"
        actions={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setIsEvaluateDialogOpen(false);
                runEvaluatorRequest.clear();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!agentId || !selectedSession?.id || !selectedEvaluatorId) {
                  return;
                }
                const result = await runEvaluatorRequest.fetch(`/v1/agents/${agentId}/sessions/${selectedSession.id}/evaluate`, {
                  method: "POST",
                  body: {
                    evaluatorIds: [selectedEvaluatorId],
                  },
                  headers: {
                    "Content-Type": "application/json",
                  },
                });
                if (!result.error) {
                  setIsEvaluateDialogOpen(false);
                }
              }}
              isDisabled={!selectedSession?.id || !selectedEvaluatorId || runEvaluatorRequest.isLoading || evaluators.length === 0}
              isLoading={runEvaluatorRequest.isLoading}
              className="!h-10 !shadow-none bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            >
              Evaluate
            </Button>
          </>
        }
      >
        <View className="space-y-4 p-4">
          {evaluatorsIsLoading && evaluators.length === 0 ? (
            <Text className="text-sm text-gray-500 dark:text-gray-400">Loading evaluators...</Text>
          ) : evaluators.length === 0 ? (
            <Text className="text-sm text-gray-500 dark:text-gray-400">No evaluators found. Create one from the Evaluators dashboard.</Text>
          ) : (
            <DropdownField
              label="Evaluator"
              options={evaluators.map((item: Evaluator) => ({ value: item.id, label: item.title }))}
              value={selectedEvaluatorId}
              onChange={setSelectedEvaluatorId}
              placeholder="Select an evaluator"
            />
          )}

          {runEvaluatorRequest.error ? (
            <View className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3">
              <Text className="text-sm text-red-700 dark:text-red-300">{runEvaluatorRequest.error.message}</Text>
            </View>
          ) : null}
        </View>
      </Dialog>

      <Dialog
        isOpen={selectedEvaluation !== null}
        onClose={() => setSelectedEvaluationId(null)}
        size="flex"
        className="w-[50vw]"
      >
        {selectedEvaluation && (
          <EvaluationDetailView
            evaluation={selectedEvaluation}
            evaluator={evaluators.find((item) => item.id === selectedEvaluation.evaluatorId) || null}
            agentEnvironmentEntities={agentEnvironmentEntities}
            agentId={agentId}
            showHeader={true}
            onClose={() => setSelectedEvaluationId(null)}
            sessionEnvironmentId={selectedSession?.agentEnvironmentId}
            sessionOrigin={selectedSession?.origin}
            sessionStatus={selectedSession?.status}
            sessionCreatedAt={selectedSession?.createdAt}
          />
        )}
      </Dialog>
      {agentId && (
        <ExportTranscriptsDialog
          agentId={agentId}
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
        />
      )}
    </View>
  );
};
